import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';
import { User } from '../types/models';
import { useNavigate } from '@tanstack/react-router';

export const useUsers = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch user details
  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        // Fetch user data
        const userResponse = await apiHandler.get('/user');
        
        // Immediately fetch org data
        const orgResponse = await apiHandler.get('/org');
        
        // Check LinkedIn URL status
        if (
          orgResponse.data && 
          (orgResponse.data.linkedin_profile_url === null || 
           orgResponse.data.linkedin_profile_url === '')
        ) {
          navigate({ to: '/linkedinconnect' });
        }
        
        return userResponse.data;
      } catch (error) {
        // Handle API errors
        console.error('Error fetching user/org data:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Update user details
  const updateUser = useMutation({
    mutationFn: async (updatedUser: Partial<User>) => {
      const response = await apiHandler.patch('/user', updatedUser);
      return response.data;
    },
    onSuccess: (newUser) => {
      queryClient.setQueryData(['user'], newUser);
    },
  });

  return {
    user,
    isLoading,
    error,
    updateUser: updateUser.mutateAsync,
    isUpdating: updateUser.isPending,
  };
};
